'use client';

import { useEffect, useRef, useState } from 'react';

/** Полоса прогресса чтения под верхним краем окна. */
export function ReadingProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const root = document.documentElement;
      const max = root.scrollHeight - root.clientHeight;
      const ratio = max > 0 ? root.scrollTop / max : 0;
      if (barRef.current) barRef.current.style.width = `${(ratio * 100).toFixed(2)}%`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return <div ref={barRef} className="progress" />;
}

/**
 * Поведение текста доклада: блоки .reveal проявляются при появлении на экране,
 * в боковом оглавлении подсвечивается раздел, который сейчас читают.
 */
export function ReportEffects({ rootId }: { rootId: string }) {
  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return;

    const reveals = Array.from(root.querySelectorAll('.reveal'));
    const revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    reveals.forEach((el) => revealObserver.observe(el));

    const railLinks = Array.from(root.querySelectorAll<HTMLAnchorElement>('#rail-list a'));
    const spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const active = railLinks.find((a) => a.getAttribute('href') === `#${entry.target.id}`);
          if (!active) continue;
          railLinks.forEach((a) => a.classList.toggle('active', a === active));
        }
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    root.querySelectorAll('.section[id]').forEach((section) => spy.observe(section));

    return () => {
      revealObserver.disconnect();
      spy.disconnect();
    };
  }, [rootId]);

  return null;
}

/** Плавающие кнопки: «Наверх» и «Разделы» с выпадающим оглавлением. */
export function TocFab({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && target !== toggleRef.current) setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  return (
    <>
      <div
        ref={panelRef}
        id="tocPanel"
        className={`fab-panel ${open ? 'open' : ''}`}
        aria-hidden={!open}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a')) setOpen(false);
        }}
      >
        <h3>Разделы</h3>
        <ol>{children}</ol>
      </div>
      <div className="fab">
        <button
          type="button"
          className={`top ${showTop ? 'show' : ''}`}
          aria-label="Наверх"
          title="Наверх"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
        <button
          ref={toggleRef}
          type="button"
          className="menu"
          aria-expanded={open}
          aria-controls="tocPanel"
          onClick={() => setOpen((value) => !value)}
        >
          Разделы
        </button>
      </div>
    </>
  );
}

/** Инфографика: если файл не загрузился, рамка показывает подпись-заглушку. */
export function FigureImage({ src, alt }: { src: string; alt: string }) {
  const imgRef = useRef<HTMLImageElement>(null);
  const markMissing = () => imgRef.current?.closest('.fig-frame')?.classList.add('is-missing');

  // Ошибка загрузки могла случиться до гидрации, когда onError ещё не подключён.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) markMissing();
  }, []);

  // eslint-disable-next-line @next/next/no-img-element -- вёрстка доклада завязана на обычный <img>
  return <img ref={imgRef} src={src} alt={alt} onError={markMissing} />;
}
